import { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, RotateCcw, Download } from "lucide-react";
import dayjs from "dayjs";
import { SectionHeader } from "./SectionHeader";
import { useRecords } from "@/hooks/useDashboard";
import { inrFull, prettyLoc } from "@/lib/format";

const EASE = [0.16, 1, 0.3, 1];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const Sel = ({ value, onChange, options, testId }) => (
  <select
    data-testid={testId}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="bg-white border border-slate-200 text-xs font-semibold text-slate-700 rounded-full px-4 py-2.5 pr-8 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] cursor-pointer appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27%2364748b%27 stroke-width=%272%27%3e%3cpolyline points=%276 9 12 15 18 9%27/%3e%3c/svg%3e')] bg-[length:14px] bg-[right_10px_center] bg-no-repeat"
  >
    {options.map((o) => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
);

export const RecordsTable = ({ meta, number = "04", single = false, topLocations = [] }) => {
  const [location, setLocation] = useState("all");
  const [fy, setFy] = useState("all");
  const [month, setMonth] = useState("all");
  const [page, setPage] = useState(1);

  const effLocation = location !== "all" ? location : topLocations.length ? [...topLocations].sort().join(",") : "all";
  useEffect(() => {
    setPage(1);
  }, [effLocation]);

  const filters = useMemo(() => ({ location: effLocation, fy, month, page, limit: 15 }), [effLocation, fy, month, page]);
  const { result, loading } = useRecords(filters);

  const set = (fn) => (v) => { fn(v); setPage(1); };
  const reset = () => { setLocation("all"); setFy("all"); setMonth("all"); setPage(1); };
  const exportUrl = `${process.env.REACT_APP_BACKEND_URL}/api/records/export?location=${encodeURIComponent(effLocation)}&fy=${encodeURIComponent(fy)}&month=${month}`;

  const cols = ["Date", "Day", ...(single ? [] : ["Location"]), "Gold", "Diamond", "Precious", "Silver", "Grand Total", "Coins", "Excl. Coins", "GP", "Royalty", "GP − Roy"];
  const nCols = cols.length;

  return (
    <section id="ledger" data-testid="ledger-section" className="bg-slate-50 border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-24">
        <SectionHeader number={number} title="Ledger" subtitle="Daily ledger.">
          <div className="flex flex-wrap gap-2 items-center">
            {!single && (
              <Sel
                testId="table-filter-location"
                value={location}
                onChange={set(setLocation)}
                options={[{ value: "all", label: "All Locations" }, ...(meta?.locations || []).map((l) => ({ value: l, label: prettyLoc(l) }))]}
              />
            )}
            <Sel
              testId="table-filter-fy"
              value={fy}
              onChange={set(setFy)}
              options={[{ value: "all", label: "All FY" }, ...(meta?.fys || []).map((f) => ({ value: f, label: f }))]}
            />
            <Sel
              testId="table-filter-month"
              value={month}
              onChange={set(setMonth)}
              options={[{ value: "all", label: "All Months" }, ...MONTHS.map((m, i) => ({ value: String(i + 1), label: m }))]}
            />
            <button
              onClick={reset}
              data-testid="table-filter-reset"
              className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900 px-3 py-2.5 transition-colors"
            >
              <RotateCcw size={12} /> Reset
            </button>
            <a
              href={exportUrl}
              data-testid="export-csv-btn"
              className="flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold px-4 py-2.5 hover:bg-[#B08D26] transition-colors"
            >
              <Download size={12} /> CSV
            </a>
          </div>
        </SectionHeader>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="hidden md:block bg-white border border-slate-200 shadow-sm overflow-x-auto"
          data-testid="records-table"
        >
          <table className="w-full text-sm min-w-[1180px]">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/60">
                {cols.map((c, i) => (
                  <th key={c} className={`px-4 py-3.5 text-[10px] uppercase tracking-[0.15em] font-bold text-slate-400 whitespace-nowrap ${i < (single ? 2 : 3) ? "text-left" : "text-right"}`}>
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={nCols} className="px-4 py-12 text-center text-slate-400 text-xs" data-testid="table-loading">Loading records…</td></tr>
              )}
              {!loading && (!result || result.records.length === 0) && (
                <tr><td colSpan={nCols} className="px-4 py-12 text-center text-slate-400 text-xs" data-testid="table-empty">No records match these filters.</td></tr>
              )}
              {!loading && result?.records.map((r) => (
                <tr key={`${r.date}-${r.location}`} className="border-b border-slate-100 hover:bg-amber-50/40 transition-colors" data-testid="record-row">
                  <td className="px-4 py-3 font-semibold text-slate-900 whitespace-nowrap">{dayjs(r.date).format("DD MMM YYYY")}</td>
                  <td className="px-4 py-3 text-slate-500">{r.day}</td>
                  {!single && <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{prettyLoc(r.location)}</td>}
                  <td className="px-4 py-3 text-right tabular-nums text-slate-700">{inrFull(r.gold)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-700">{inrFull(r.diamond)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-700">{inrFull(r.precious)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-700">{inrFull(r.silver)}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-bold text-slate-900">{inrFull(r.grand_total)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-500">{inrFull(r.coins)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-700">{inrFull(r.total_excl_coins)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-emerald-700">{inrFull(r.gp)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-500">{inrFull(r.royalty)}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold text-[#B08D26]">{inrFull(r.gp_after_royalty)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>

        <div className="md:hidden space-y-3" data-testid="records-cards">
          {loading && <p className="text-center text-slate-400 text-xs py-10">Loading records…</p>}
          {!loading && (!result || result.records.length === 0) && (
            <p className="text-center text-slate-400 text-xs py-10">No records match these filters.</p>
          )}
          {!loading &&
            result?.records.map((r) => (
              <div key={`${r.date}-${r.location}`} className="bg-white border border-slate-200 shadow-sm p-4" data-testid="record-card">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-bold text-slate-900">
                    {dayjs(r.date).format("DD MMM YYYY")} <span className="text-slate-400 font-normal">· {r.day}</span>
                  </p>
                  {!single && <p className="text-[11px] font-semibold text-slate-500">{prettyLoc(r.location)}</p>}
                </div>
                <p className="font-display text-xl font-semibold text-slate-900 tabular-nums mb-3">{inrFull(r.grand_total)}</p>
                <div className="grid grid-cols-3 gap-x-3 gap-y-2">
                  {[
                    ["Gold", r.gold],
                    ["Diamond", r.diamond],
                    ["Precious", r.precious],
                    ["Silver", r.silver],
                    ["Coins", r.coins],
                    ["Excl. Coins", r.total_excl_coins],
                    ["GP", r.gp],
                    ["Royalty", r.royalty],
                    ["GP − Roy", r.gp_after_royalty],
                  ].map(([l, v]) => (
                    <div key={l}>
                      <p className="text-slate-400 uppercase tracking-wide text-[9px] font-semibold">{l}</p>
                      <p className="tabular-nums text-slate-700 text-xs font-medium">{inrFull(v)}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>

        <div className="flex items-center justify-between mt-6">
          <p className="text-xs text-slate-500 tabular-nums" data-testid="table-pagination-info">
            {result ? `Page ${result.page} of ${result.pages} · ${result.total.toLocaleString("en-IN")} records` : "—"}
          </p>
          <div className="flex gap-2">
            <button
              data-testid="table-prev-btn"
              disabled={!result || page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="flex items-center gap-1 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 disabled:opacity-40 hover:border-slate-400 transition-colors"
            >
              <ChevronLeft size={13} /> Prev
            </button>
            <button
              data-testid="table-next-btn"
              disabled={!result || page >= result.pages}
              onClick={() => setPage((p) => p + 1)}
              className="flex items-center gap-1 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 disabled:opacity-40 hover:border-slate-400 transition-colors"
            >
              Next <ChevronRight size={13} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
